import { configureStore } from '@reduxjs/toolkit';
import { offline } from '@redux-offline/redux-offline';
import offlineConfig from '@redux-offline/redux-offline/lib/defaults';
import { reduxEffect } from '@/lib/api-client';
import { FLUSH, PAUSE, PERSIST, persistReducer, PURGE, REGISTER, REHYDRATE } from 'redux-persist';

// Import all slice reducers
import {
  appStateReducer,
  bookingsReducer,
  checkinsReducer,
  clientsReducer,
  documentsReducer,
  feedbackReducer,
  filesReducer,
  hsDocumentsReducer,
  jobsReducer,
  materialsReducer,
  notesReducer,
  notificationReducer,
  photosReducer,
  smallworksReducer,
  statesReducer,
  tasksReducer,
  userReducer,
} from './slices';

const rootReducer = {
  appState: appStateReducer,
  bookings: bookingsReducer,
  checkins: checkinsReducer,
  clients: clientsReducer,
  documents: documentsReducer,
  feedback: feedbackReducer,
  files: filesReducer,
  hs_documents: hsDocumentsReducer,
  jobs: jobsReducer,
  materials: materialsReducer,
  notes: notesReducer,
  notification: notificationReducer,
  photos: photosReducer,
  smallworks: smallworksReducer,
  states: statesReducer,
  tasks: tasksReducer,
  user: userReducer,
};

export interface RootState {
  appState: ReturnType<typeof appStateReducer>;
  bookings: ReturnType<typeof bookingsReducer>;
  checkins: ReturnType<typeof checkinsReducer>;
  clients: ReturnType<typeof clientsReducer>;
  documents: ReturnType<typeof documentsReducer>;
  feedback: ReturnType<typeof feedbackReducer>;
  files: ReturnType<typeof filesReducer>;
  hs_documents: ReturnType<typeof hsDocumentsReducer>;
  jobs: ReturnType<typeof jobsReducer>;
  materials: ReturnType<typeof materialsReducer>;
  notes: ReturnType<typeof notesReducer>;
  notification: ReturnType<typeof notificationReducer>;
  photos: ReturnType<typeof photosReducer>;
  smallworks: ReturnType<typeof smallworksReducer>;
  states: ReturnType<typeof statesReducer>;
  tasks: ReturnType<typeof tasksReducer>;
  user: ReturnType<typeof userReducer>;
}

export const createAppStore = (persistCallback?: () => void) => {
  const appOfflineConfig = {
    ...offlineConfig,
    effect: (effect: any, _action: any) => reduxEffect(effect, _action),
    persistOptions: {
      keyPrefix: "YHS::",
      blacklist: ['appState'],
    },
    persistCallback: persistCallback || (() => {}),
    queue: {
      ...offlineConfig.queue,
      dequeue: (outbox: any[], item: any, context: any) => {
        if (
          context &&
          context.offline &&
          context.offline.retryCount &&
          context.offline.retryCount >= 3
        ) {
          return outbox;
        }
        const [, ...rest] = outbox;
        return rest;
      },
      peek: (outbox: any[], item: any, context: any) => {
        if (
          context &&
          context.offline &&
          context.offline.retryCount &&
          context.offline.retryCount >= 3
        ) {
          return false;
        }
        return outbox[0];
      },
    },
  };

  // Action logging can be enabled for debugging if needed
  const actionLogger = (store: any) => (next: any) => (action: any) => {
    console.log('🔄 Redux Action Dispatched:', action.type, action);
    return next(action);
  };

  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [
            'persist/FLUSH',
            'persist/PAUSE',
            'persist/PERSIST',
            'persist/PURGE',
            'persist/REGISTER',
            'persist/REHYDRATE',
            'feedback/addFeedback',
            'feedback/updateFeedback',
            // Redux Offline actions that may contain File objects
            'photos/uploadPhoto',
            'Offline/SCHEDULE_RETRY',
            'Offline/COMPLETE_RETRY',
            'Offline/BUSY',
            'Offline/ONLINE',
          ],
          ignoredPaths: [
            // Ignore Redux Offline outbox where FormData/File objects are stored
            'offline.outbox',
            'offline.retryToken',
            // Also ignore any file-related paths
            'photos.items.fileObject',
            'photos.items.originalFileObject',
          ],
        },
      }).concat(actionLogger),
    enhancers: (getDefaultEnhancers) =>
      getDefaultEnhancers().concat(offline(appOfflineConfig)),
  });
};

export type AppStore = ReturnType<typeof createAppStore>;
export type AppDispatch = AppStore['dispatch'];

export default createAppStore;